import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchRejectedStripePayouts,
  fetchRejectedStripePayoutsMethodFilters,
  updateRejectedStripePayoutsFilters,
  disableRejectedStripePayoutsApplied,
  setSortData,
  refreshRejectedStripePayouts,
} from '../../../redux/slices/RejectedStripePayoutsSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
// import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
// import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
// import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
// import stripePayoutsApi from '../../../utils/api/stripe-payouts';

/**
 * Rejected stripe payouts components
 * @returns {any} The result
 */
function RejectedEMSPPayouts() {
  const { t } = useTranslation('stripePayout');
  const {
    payouts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.rejectedStripePayouts);
  const dispatch = useAppDispatch();
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };
  // const [exportLoader, setExportLoader] = useState(false);
  // const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchRejectedStripePayouts(data));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortField,
      sortBy,
    });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param {any} payoutsStatus - The payoutsStatus
       * @returns {any} The result
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div>{ToFixedValue(payoutsStatus.amount)}</div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'rejectedAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
  ];

  /**
   * Current columns for export
   */
  // const currentColumns = [
  //   { label: t('sNo'), accessorKey: '__serial__' },
  //   { label: t('accountID'), accessorKey: 'accountCustomId' },
  //   { label: t('accountName'), accessorKey: 'accountName' },
  //   { label: t('email'), accessorKey: 'email' },
  //   { label: t('revenueFromDate'), accessorKey: 'fromDate' },
  //   { label: t('revenueToDate'), accessorKey: 'toDate' },
  //   { label: t('payoutAmount'), accessorKey: 'amount' },
  //   { label: t('payoutDate'), accessorKey: 'rejectedAt' },
  // ];

  /**
   * Filtering columns for export without hidden columns
   */
  // const exportCurrentColumns = currentColumns.filter(
  //   (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  // );

  /**
   * Export data pdf or csv function
   * @param {string} type - The type of export
   */
  // const exportDataPdfOrCSV = (type: string) => {
  //   setExportLoader(true);
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   stripePayoutsApi
  //     .exportStripePayouts({
  //       ...queryParameters,
  //       submissionStatus: 'rejected',
  //       sortBy,
  //       sortField,
  //     })
  //     .then((response) => {
  //       const headers = exportCurrentColumns.map((col) => col.label);
  //       const rows = response.data.map((row: StripePayoutsInterface, index: number) =>
  //         exportCurrentColumns.map((col) => {
  //           switch (col.accessorKey) {
  //             case '__serial__':
  //               return index + 1;

  //             case 'fromDate':
  //               return row.fromDate ? ConvertUTCtoDate(row.fromDate) : t('na');

  //             case 'toDate':
  //               return row.toDate ? ConvertUTCtoDate(row.toDate) : t('na');

  //             case 'rejectedAt':
  //               return (row as any).rejectedAt ? ConvertUTCtoDate((row as any).rejectedAt) : t('na');

  //             case 'amount':
  //               return row.amount ? ToFixedValue(row.amount) : '0.00';

  //             default:
  //               return (row as any)[col.accessorKey] || t('na');
  //           }
  //         }),
  //       );
  //       setExportLoader(false);
  //       if (rows.length > 0) {
  //         if (type === 'csv') {
  //           exportCSV(headers, rows, 'rejected-payouts-list.csv');
  //         } else {
  //           exportPDF(headers, rows, 'rejected-payouts-list.pdf');
  //         }
  //       }
  //     })
  //     .catch(() => {
  //       setExportLoader(false);
  //     });
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshRejectedStripePayouts({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0 || dateRange.startDate || dateRange.endDate) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
      });
      dispatch(disableRejectedStripePayoutsApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('emspPayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-container">
      <CustomTable
        header={column}
        version="v2"
        data={payouts}
        serialNo
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateRejectedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRejectedStripePayoutsMethodFilters}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel="Refresh"
        height={320}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        // exportLoader={exportLoader}
        // exportDataPdfOrCSV={permissionCheck('mobilityPayoutsExport') ? exportDataPdfOrCSV : undefined}
        // onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default RejectedEMSPPayouts;
